import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
} from '@chakra-ui/form-control';
import { cx } from '@chakra-ui/utils';
import {
  Box,
  Input,
  InputGroup,
} from '@chakra-ui/react';
import formatStringByPattern from 'format-string-by-pattern';

import { useCommonOptionsStore } from '../../store';
import { useTranslations } from '../../translations/useTranslations';

import { InputControlProps } from './types';

const formatInput = (
  format: string,
  cardNumber: string,
) => formatStringByPattern(format, cardNumber.replace(/\D/g, ''));

export const InputControl = React.forwardRef<HTMLInputElement, any>(({
  name: fieldName,
  label,
  helper,
  error,
  isDisabled,
  isRequired,
  mask,
  rules,
  ...props
}: InputControlProps, forwardRef) => {
  const {
    register,
    getValues,
    setValue,
  } = useFormContext();

  const lang = useCommonOptionsStore(state => state.lang);
  const [t] = useTranslations(lang);

  const registerOptions = {
    ...(isRequired && { required: t('requiredField') }),
    ...rules,
    onBlur: () => {
      if (props.onBlur) props.onBlur(fieldName as any);
    },
    onChange: () => {
      if (mask) {
        const val = getValues(fieldName!);
        setValue(fieldName!, formatInput(mask, val));
      }
      if (props.onChange) props.onChange(fieldName as any);
    },
  };

  const { ref: inputRef, ...rest } = register(fieldName!, { ...registerOptions });

  const mergedRef = (e: any) => {
    inputRef(e);
    if (forwardRef) (forwardRef as any).current = e;
  };

  return (
    <Box>
      <FormControl
        isInvalid={!!error}
        isDisabled={isDisabled}
        isRequired={isRequired}
        className={cx('InputContainer')}
      >
        <FormLabel className={cx('InputLabel')}>
          {label}
        </FormLabel>
        <InputGroup>
          <Input
            className={cx('Input')}
            {...props}
            {...rest}
            ref={mergedRef}
          />
        </InputGroup>

        {helper && !error && <FormHelperText className={cx('InputHelper')}>{helper}</FormHelperText>}
        {error && <FormErrorMessage className={cx('InputError')}>{error}</FormErrorMessage>}
      </FormControl>
    </Box>
  );
});
